import type {
  AnalyticsPayload,
  DefaultParameters,
  LabelAnalytics,
} from '@/types/analytics';
import { pascalCase, skewerCase } from '@utils/helpers';

/**
 * Framework-free analytics layer.
 *
 * The legacy app used `@app-analytics/analytics`, but that SDK (v4) is an
 * Angular library: its public entry (`ItauDigitalAnalyticsView`) only notifies
 * an RxJS subject whose subscriber is wired by Angular DI. In a vanilla app the
 * subject has no subscriber, so `track()` would be a no-op.
 *
 * To keep the GTM `dataLayer` contract **identical** we reproduce here exactly
 * what the SDK does internally: load GTM from `window._containerGTM`, normalise
 * the payload (`parseAnalyticsData`) and push it (`pushWeb`).
 *
 * NOTE: validate with the DigitalAnalytics (DAS) team before go-live.
 */

const SDK_ANALYTICS_WEB_VERSION = '4.0.1';
const SQUAD_TAG = 'Martech/Produtos Martech';
const PAGE_PREFIX = 'IT:NL:IN:SocialLink';

export class AnalyticsService {
  private pageTitle = '';

  constructor(private readonly gtmContainer: string) {
    this.loadGTM();
    this.setDefaultParameters({
      parameters: {
        business: 'institucional',
        environment: 'nao-logado',
        business_channel: 'institucional',
      },
    });
  }

  pageLoad(pageName: string): void {
    this.pageTitle = pascalCase(pageName);
    this.track({
      rule: 'pageLoad',
      page: {
        nome: `${PAGE_PREFIX}:${this.pageTitle}:${this.formatPageName()}`,
        url: document.location.href,
      },
      custom: { squadresponsaveltag: SQUAD_TAG },
      event_name: 'screen_view',
      parameters: {
        custom_path: `/${this.formatPageNameGA4()}`,
        implementation_team: 'produtos-martech:martech',
        description: 'pagina-com-diversos-links-e-icones-de-redes-sociais',
        flow: 'social-link',
      },
    });
  }

  event(label: LabelAnalytics): void {
    const labelText = pascalCase(label.link.label).replace(
      /[.,\/#!$%\^&\*;{}=\-_`~()]/g,
      '',
    );
    const action = pascalCase(label.link.action);
    const detail = skewerCase(label.link.label);

    this.track({
      rule: 'customLink',
      page: {
        nome: `${PAGE_PREFIX}:${this.pageTitle}:${this.formatPageName()}`,
        url: document.location.href,
      },
      custom: { squadresponsaveltag: SQUAD_TAG },
      events: {
        category: label.link.category || 'Clique',
        action: `SocialLink:${action}`,
        label: labelText,
      },
      event_name: 'click',
      parameters: {
        custom_path: `/${this.formatPageNameGA4()}`,
        implementation_team: 'produtos-martech:martech',
        description: 'pagina-com-diversos-links-e-icones-de-redes-sociais',
        detail,
        flow: 'social-link',
      },
    });
  }

  private setDefaultParameters(param: DefaultParameters): void {
    const ad = { ...param.parameters, sdk_analytics_web_version: SDK_ANALYTICS_WEB_VERSION, event: 'dataLayerUpdate' };
    this.ensureDataLayer().push(ad as Record<string, unknown>);
  }

  private track(payload: AnalyticsPayload): void {
    const analyticsData = this.parseAnalyticsData(payload);
    this.pushWeb(analyticsData);
  }

  private parseAnalyticsData(obj: AnalyticsPayload): Record<string, unknown> {
    return {
      ...obj,
      sdk_analytics_web_version: SDK_ANALYTICS_WEB_VERSION,
      site: { ambiente: '', nome: '', negocio: '', tipoDeCanal: '' },
      page: {
        ambiente: '',
        loginState: '',
        secao: '',
        subSecao1: '',
        subSecao2: '',
        ...obj.page,
        url: obj.page.url || document.location.href,
      },
      rule: obj.rule,
      event: 'dataLayerUpdate',
      _tag: '',
      custom: obj.custom ?? {},
      nomePagina: '',
      portal: '',
    };
  }

  private pushWeb(analyticsData: Record<string, unknown>): void {
    const dataLayer = this.ensureDataLayer();
    const ad = JSON.parse(JSON.stringify(analyticsData)) as Record<string, unknown>;
    ad.analyticsData = JSON.parse(JSON.stringify(analyticsData));
    dataLayer.push(ad);
  }

  private loadGTM(): void {
    const container = this.gtmContainer?.replace('GTM-', '');
    if (!container) return;

    const dataLayer = this.ensureDataLayer();
    const id = `GTM-${container}`;

    if (!document.getElementById('gtm-script')) {
      const tag = document.createElement('script');
      tag.id = 'gtm-script';
      tag.async = true;
      tag.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
      document.getElementsByTagName('head')[0]?.appendChild(tag);
    }

    dataLayer.push({
      originalLocation:
        document.location.protocol +
        '//' +
        document.location.hostname +
        document.location.pathname +
        document.location.search,
    });
    dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  }

  private ensureDataLayer(): Record<string, unknown>[] {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  private formatPageName(): string {
    const path = document.location.pathname.split('/').filter(Boolean);
    return path
      .filter((item) => item !== 'social-link')
      .map((segment) =>
        segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(''),
      )
      .join('');
  }

  private formatPageNameGA4(): string {
    return document.location.pathname.split('/').filter(Boolean).join('-');
  }
}

export const analyticsService = new AnalyticsService(
  import.meta.env.VITE_GTM_CONTAINER,
);
